import { useEffect } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { generatedImagesQueries } from '../../../../../queries/generatedImages';
import { generatedAudioQueries } from '../../../../../queries/generatedAudio';
import { generatedFileQueries } from '../../../../../queries/generatedFile';
import { GeneratedImagesService } from '../../../../../service/GeneratedImagesService';
import { GeneratedAudioService } from '../../../../../service/GeneratedAudioService';
import { GeneratedFileService } from '../../../../../service/GeneratedFileService';
import type { Space } from '../../../../../util/Types/Sidebar';

function useDeleteAssetMutation(
  removeFn: (id: string) => Promise<unknown>,
  queryKey: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => removeFn(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });
}

function useGeneratedAssetsQueries(
  spaces: Space[],
  refresh: () => void,
) {
  const queryClient = useQueryClient();

  const { data: privateImagesData } = useQuery(generatedImagesQueries.all());
  const spaceImageResults = useQueries({
    queries: spaces.map(space => generatedImagesQueries.all(space.id)),
  });

  const { data: privateAudioData } = useQuery(generatedAudioQueries.all());
  const spaceAudioResults = useQueries({
    queries: spaces.map(space => generatedAudioQueries.all(space.id)),
  });

  const { data: privateFilesData } = useQuery(generatedFileQueries.all());
  const spaceFileResults = useQueries({
    queries: spaces.map(space => generatedFileQueries.all(space.id)),
  });

  const { mutate: deleteGeneratedImage } = useDeleteAssetMutation(
    GeneratedImagesService.remove,
    'generated-images',
  );
  const { mutate: deleteGeneratedAudio } = useDeleteAssetMutation(
    GeneratedAudioService.remove,
    'generated-audio',
  );
  const { mutate: deleteGeneratedFile } = useDeleteAssetMutation(
    GeneratedFileService.remove,
    'generated-files',
  );

  useEffect(() => {
    const onRefresh = () => {
      queryClient.cancelQueries({ queryKey: ['generated-images'] });
      queryClient.cancelQueries({ queryKey: ['generated-audio'] });
      queryClient.cancelQueries({ queryKey: ['generated-files'] });

      refresh();
      queryClient.invalidateQueries({ queryKey: ['generated-images'] });
      queryClient.invalidateQueries({ queryKey: ['generated-audio'] });
      queryClient.invalidateQueries({ queryKey: ['generated-files'] });
    };
    window.addEventListener('sidebar-refresh', onRefresh);
    return () => window.removeEventListener('sidebar-refresh', onRefresh);
  }, [refresh, queryClient]);

  return {
    privateImages: privateImagesData?.data ?? [],
    privateAudio: privateAudioData?.data ?? [],
    privateFiles: privateFilesData?.data ?? [],
    spaceImageResults,
    spaceAudioResults,
    spaceFileResults,
    deleteGeneratedImage,
    deleteGeneratedAudio,
    deleteGeneratedFile,
    queryClient,
  };
}

export { useGeneratedAssetsQueries };
